import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../utils/api';
import { toast } from 'sonner';
import CosmicBackground from '../components/CosmicBackground';
import ShareCard from '../components/ShareCard';
import Logo from '../components/Logo';
import { LogOut, Moon, Eye, Brain, Zap, ArrowLeft, ChevronDown, ChevronUp, Share2 } from 'lucide-react';
import LanguageSelector from '../components/LanguageSelector';
import { useLanguage } from '../contexts/LanguageContext';
import { useTranslation } from '../utils/translations';

const safeText = (value) => (typeof value === 'string' ? value : '');

const shortText = (value, max = 150) => {
  const text = safeText(value).trim();
  if (!text) return '';
  return text.length > max ? `${text.substring(0, max)}...` : text;
};

const DreamPortal = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { t } = useTranslation(language);

  const [user, setUser] = useState(null);
  const [dreamText, setDreamText] = useState('');
  const [interpretation, setInterpretation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareContent, setShareContent] = useState(null);

  const fetchHistory = async (currentLanguage = language) => {
    setHistoryLoading(true);
    try {
      const response = await api.get(`/universe/dream-history?language=${currentLanguage}`);
      setHistory(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Failed to fetch dream history', error);
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');

    if (!token || !userData) {
      navigate('/login');
      return;
    }

    try {
      const parsedUser = JSON.parse(userData);
      setUser(parsedUser);
    } catch (error) {
      console.error('Failed to parse user data', error);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      navigate('/login');
      return;
    }

    fetchHistory(language);
  }, [navigate]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');

    if (!token || !userData) return;

    fetchHistory(language);
  }, [language]);

  const buildShareContent = (dream) => {
    const symbolic = safeText(dream?.symbolic_meaning);
    const psychological = safeText(dream?.psychological_meaning);
    const energetic = safeText(dream?.energetic_interpretation);

    return [
      `${t('symbolicMeaning')}: ${symbolic}`,
      `${t('psychologicalMeaning')}: ${psychological}`,
      `${t('energeticInterpretation')}: ${energetic}`,
    ].join('\n\n');
  };

  const handleInterpretDream = async (e) => {
  e.preventDefault();

  if (!dreamText.trim() || loading) return;

  setLoading(true);

  try {
    const response = await api.post('/universe/dream-portal', {
      dream_description: dreamText.trim(),
      language,
    });

    if (!response?.data) {
      throw new Error('Empty response');
    }

    setInterpretation(response.data);

    setTimeout(() => {
      setDreamText('');
    }, 200);

    await fetchHistory();

  } catch (error) {
    console.error(error);

    toast.error(
      error?.response?.data?.detail ||
      'Server slow. Please try again.'
    );
  } finally {
    setLoading(false);
  }
};

  const handleLogout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      console.error('Logout API call failed:', error);
    }

    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
  };

  if (!user) return null;

  return (
    <div className="min-h-screen relative">
      <CosmicBackground />
      <div
        className="fixed inset-0 bg-gradient-to-b from-[#1e3a8a] via-[#0f172a] to-black pointer-events-none"
        style={{ zIndex: 1 }}
      />

      <div className="relative z-10">
       <header
         className="px-6 sm:px-12 border-b border-white/5"
         style={{
           paddingTop: 'max(env(safe-area-inset-top), 20px)',
           paddingBottom: '24px'
         }}
         >
          <div className="max-w-7xl mx-auto flex justify-between items-center">
            <div className="flex items-center gap-6">
              <button
                onClick={() => navigate('/dashboard')}
                className="text-slate-400 hover:text-white transition-colors"
                data-testid="back-button"
              >
                <ArrowLeft size={20} />
              </button>
              <Logo size="md" />
            </div>
            <div className="flex items-center gap-6">
              <LanguageSelector />
              <span className="text-slate-300 text-sm hidden sm:inline" data-testid="user-name">
                {t('welcome')}, {user.full_name}
              </span>
              <button
                onClick={handleLogout}
                className="text-slate-400 hover:text-white transition-colors"
                data-testid="logout-button"
                title={t('logout')}
              >
                <LogOut size={20} />
              </button>
            </div>
          </div>
        </header>

        <div className="max-w-5xl mx-auto px-6 py-12 space-y-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center space-y-4"
          >
            <h1 className="font-heading text-4xl font-light text-white tracking-wide">
              {t('enterYourDreams')}
            </h1>
            <p className="text-slate-400 max-w-2xl mx-auto leading-relaxed">
              {t('dreamsAreMessages')}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="glass-card p-8"
            data-testid="dream-input-card"
          >
            <form onSubmit={handleInterpretDream} className="space-y-4">
              <div className="relative">
                <textarea
                  value={dreamText}
                  onChange={(e) => setDreamText(e.target.value)}
                  placeholder={t('describeDream')}
                  className="w-full px-6 py-4 bg-slate-950/50 border border-white/10 rounded-2xl text-white placeholder:text-slate-500 focus:outline-none focus:border-violet-500/50 focus:ring-2 focus:ring-violet-500/50 transition-all min-h-[150px] resize-none"
                  disabled={loading}
                  data-testid="dream-input"
                />
              </div>

              <button
                type="submit"
                disabled={loading || !dreamText.trim()}
                className="w-full py-4 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-full font-medium hover:from-violet-500 hover:to-purple-500 transition-all cosmic-glow disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                data-testid="interpret-button"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                    <span>{t('interpretingDream')}</span>
                  </>
                ) : (
                  <>
                    <Eye size={18} />
                    <span>{t('revealInterpretation')}</span>
                  </>
                )}
              </button>
            </form>
          </motion.div>

          <AnimatePresence>
            {interpretation && (
              <motion.div
                id="interpretation-section"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="space-y-6"
              >
                <div className="flex justify-end">
                  <button
                    onClick={() => {
                      setShareContent({
                        title: shortText(interpretation.dream_description, 80) || t('yourDream'),
                        content: buildShareContent(interpretation),
                        date: interpretation.created_at,
                        type: 'dream'
                      });
                      setShareModalOpen(true);
                    }}
                    className="px-4 py-2 bg-violet-500/20 text-violet-400 hover:bg-violet-500/30 rounded-lg transition-all flex items-center gap-2 text-sm"
                    data-testid="share-interpretation-btn"
                  >
                    <Share2 size={16} />
                    <span>{t('share')}</span>
                  </button>
                </div>

                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 }}
                  className="glass-card p-8"
                  data-testid="symbolic-section"
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      <Moon className="w-10 h-10 text-amber-400" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-heading text-xl font-bold text-amber-400 mb-3">
                        {t('symbolicMeaning')}
                      </h3>
                      <p className="text-slate-300 leading-relaxed" data-testid="symbolic-text">
                        {safeText(interpretation.symbolic_meaning)}
                      </p>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2 }}
                  className="glass-card p-8"
                  data-testid="psychological-section"
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      <Brain className="w-10 h-10 text-cyan-400" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-heading text-xl font-bold text-cyan-400 mb-3">
                        {t('psychologicalMeaning')}
                      </h3>
                      <p className="text-slate-300 leading-relaxed" data-testid="psychological-text">
                        {safeText(interpretation.psychological_meaning)}
                      </p>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 }}
                  className="glass-card p-8"
                  data-testid="energetic-section"
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      <Zap className="w-10 h-10 text-violet-400" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-heading text-xl font-bold text-violet-400 mb-3">
                        {t('energeticInterpretation')}
                      </h3>
                      <p className="text-slate-300 leading-relaxed" data-testid="energetic-text">
                        {safeText(interpretation.energetic_interpretation)}
                      </p>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4 }}
                  className="glass-card p-6 border-white/5"
                >
                  <p className="text-xs text-slate-500 mb-2">{t('yourDream')}</p>
                  <p className="text-slate-400 text-sm italic">
                    {safeText(interpretation.dream_description)}
                  </p>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {history.length > 0 && (
            <div className="text-center">
              <button
                onClick={() => setShowHistory(!showHistory)}
                className="text-violet-400 hover:text-violet-300 transition-colors font-medium flex items-center gap-2 mx-auto"
                data-testid="toggle-history-button"
              >
                {showHistory ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                <span>{showHistory ? t('hideDreamJournal') : t('viewDreamJournal')} ({history.length})</span>
              </button>
            </div>
          )}

          <AnimatePresence>
            {showHistory && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="space-y-4"
                data-testid="history-section"
              >
                {historyLoading && (
                  <div className="glass-card p-6 text-center text-slate-400">
                    {t('loading') || 'Loading...'}
                  </div>
                )}

                {!historyLoading && history.length > 0 && history.map((dream, idx) => (
                  <motion.div
                    key={dream.id || idx}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.05 }}
                    className="glass-card p-6"
                    data-testid={`history-item-${idx}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <p className="text-white font-medium italic flex-1">
                        "{shortText(dream.dream_description, 100)}"
                      </p>
                      <button
                        onClick={() => {
                          setShareContent({
                            title: shortText(dream.dream_description, 80) || t('yourDream'),
                            content: buildShareContent(dream),
                            date: dream.created_at,
                            type: 'dream'
                          });
                          setShareModalOpen(true);
                        }}
                        className="p-1.5 text-slate-500 hover:text-violet-400 hover:bg-violet-500/10 rounded-lg transition-all ml-2 flex-shrink-0"
                        data-testid={`share-dream-${idx}`}
                        title={t('share')}
                      >
                        <Share2 size={14} />
                      </button>
                    </div>

                    <div className="space-y-2 text-sm">
                      <p className="text-amber-400">🌙 {shortText(dream.symbolic_meaning, 150)}</p>
                      <p className="text-cyan-400">🧠 {shortText(dream.psychological_meaning, 150)}</p>
                      <p className="text-violet-400">⚡ {shortText(dream.energetic_interpretation, 150)}</p>
                    </div>

                    <p className="text-slate-600 text-xs mt-3">
                      {dream.created_at ? new Date(dream.created_at).toLocaleString() : ''}
                    </p>
                  </motion.div>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      <ShareCard
        isOpen={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        title={shareContent?.title || ''}
        content={shareContent?.content || ''}
        date={shareContent?.date || new Date().toISOString()}
        type={shareContent?.type || 'dream'}
      />
    </div>
  );
};

export default DreamPortal;
